import React, { AriaAttributes, DOMAttributes, JSXElementConstructor, MouseEvent, ReactElement, Ref } from 'react';
import classNames from 'classnames';
import { BringgIcon } from '@bringg/bringg-icons';
import { DSButtonSize } from '../shared';
import s from './index.module.scss';
import { DSButtonContent } from '../button-content';

export type DSLinkButtonVariant = 'link' | 'subtle link';

/**
 * What the link button component uses for itself
 */
interface LinkButtonProps {
	variant?: DSLinkButtonVariant;

	size?: DSButtonSize;

	// If just want to display an icon button just pass the icon as children and set treatChildrenAsIcon to true if the children is not an BringgIcon icon element
	leftIcon?: React.ReactNode;
	rightIcon?: React.ReactNode;

	children?: React.ReactNode;
	treatChildrenAsIcon?: boolean;

	disabled?: boolean;
}

/**
 * The component that will be rendered as the link button must accept the following props:
 */
interface BaseComponentProps extends AriaAttributes, DOMAttributes<HTMLAnchorElement> {
	className?: string;
	ref?: Ref<HTMLAnchorElement>;
	role?: string;
}

// Done this way to allow for custom element to be passed as the component prop and have typesafety on the props of the custom component
// so you can now use custom link components like react-router-dom Link
export type DSLinkButtonProps<
	// Props of the component to render as link
	Props extends BaseComponentProps,
	// Component to render as link
	Comp extends keyof JSX.IntrinsicElements | JSXElementConstructor<Props> = 'a'
> = LinkButtonProps &
	// Add the props of the component to render as link to the props of the DSLinkButton
	Props & {
		// Which component to render as link (default is 'a')
		component?: Comp;
	};

function DSLinkButtonWithoutForwardRef<
	Comp extends keyof JSX.IntrinsicElements | JSXElementConstructor<any>,
	Props extends BaseComponentProps = React.ComponentProps<Comp>
>(
	{
		component,
		size = 'medium',
		variant,
		leftIcon,
		rightIcon,
		children,
		className,
		treatChildrenAsIcon,
		disabled,
		...nativeProps
	}: DSLinkButtonProps<Props, Comp>,
	ref: Ref<HTMLAnchorElement>
) {
	const onlyIcon =
		(children && treatChildrenAsIcon) ||
		// Either no children and only have left or right - (instead you should set the icon as children and set treatChildrenAsIcon to true if the children is not an BringgIcon icon element)
		(!children && (leftIcon || rightIcon)) ||
		// If the children is bringg icon, and not specified to not treat as icon, we treat it as an icon
		(children &&
			treatChildrenAsIcon !== false &&
			!leftIcon &&
			!rightIcon &&
			React.isValidElement(children) &&
			children.type === BringgIcon);

	if (onlyIcon && variant === undefined) {
		variant = 'subtle link';
	}

	// Using create element to be able to render custom component
	return React.createElement(
		// We don't support getting the component from global context even though it will be more convenient
		// as you will lose the type safety, so instead create a wrapper component around the DSLinkButton and pass the react-router-dom Link as the component prop
		component || 'a',
		{
			ref,

			'aria-disabled': disabled,

			// In case not using the default component, we are making sure to set the role to link
			role: 'link',
			disabled,
			...nativeProps,
			onClick(e: MouseEvent<HTMLAnchorElement>) {
				if (disabled) {
					e.preventDefault();
					return;
				}

				if (nativeProps.onClick) {
					nativeProps.onClick(e);
				}
			},
			className: classNames(
				s.link,
				{
					[s.disabled]: disabled,
					// Size
					[s.small]: size === 'small',
					[s.medium]: size === 'medium',
					[s.large]: size === 'large',

					// Type
					[s.link]: variant === 'link',
					[s.subtleLink]: variant === 'subtle link',

					[s.onlyIcon]: onlyIcon
				},
				className
			)
		},

		// Children
		<DSButtonContent leftIcon={leftIcon} rightIcon={rightIcon}>
			{children}
		</DSButtonContent>
	);
}

/**
 * Design System Link Button
 *
 * If need to use Link from react-component pass Link as component prop
 * But I advise you to create a component wrapper around the DSLinkButton and use the Link from react-component inside it, in order to have type safety
 *
 * @see https://www.figma.com/design/x3dPTVfzcPhvz8sl04NwB4/Bringg?node-id=69-1755 Figma design
 *
 *
 * Done all the casting to allow for custom types of component to render and to allow typescript to infer the props of the component by the component prop passed
 */
export const DSLinkButton = React.forwardRef(DSLinkButtonWithoutForwardRef) as <
	Comp extends
		| keyof JSX.IntrinsicElements
		| JSXElementConstructor<HTMLAnchorElement>
		| ((props: any) => ReactElement | null)
>(
	p: DSLinkButtonProps<React.ComponentProps<Comp>> & { ref?: Ref<HTMLAnchorElement> }
) => ReactElement;
